import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Modal, ModalHeader} from 'reactstrap';
import {toast} from 'react-toastify';
import {MagnifyingGlass, Plus, Warning, X} from '../../icons';
import {Spinner} from '../../Spinner';
import {LoadErrorState} from '../../elements/LoadErrorState';
import {YesNoModal} from '../../elements/YesNoModal';
import {AgencyHeader} from '../AgencyHeader';
import {AgencyTabs} from '../AgencyTabs';
import {CONNECTIONS_TAB_ACCOUNTS, ConnectionsSubTabs} from '../ConnectionsSubTabs';
import {AddClientForm} from '../AddClientModal';
import {AddClientAdAccountModal} from '../../client/ad_accounts/AddClientAdAccountModal';
import {
    isPlanLimitError, publishAdAccountQuota, quotaFromLinkError, quotaIsFull, refetchAdAccountQuota, useAdAccountQuota,
} from '../../../utils/adAccountQuota';
import {
    quotaCounterText, quotaFullTitle, recordQuotaEvent, requestMoreAccounts,
} from '../../client/control_central/accountQuota';
import {
    ACCESS_PARAM_NONE,
    KIND_PROFITABILITY,
    KIND_STORE,
    PAGE_SIZE,
    PLAN_LIMIT_CODE,
    VIEWS,
    VIEW_IGNORED,
    VIEW_LINKED,
    VIEW_UNLINKED,
    activeUnlinkedCount,
    bulkOutcome,
    candidatesByAdAccountPk,
    dailyLimitOf,
    errorCodeOf,
    filterRows,
    findIgnoreTarget,
    groupByClient,
    groupUnlinked,
    linkedClientIdsOf,
    normalizeAccounts,
    pageGroups,
    paramOfView,
    parseIgnoreParam,
    platformChips,
    platformFromParam,
    pruneSelection,
    reconnectKindOf,
    rowsIn,
    runInSeries,
    sortIgnored,
    suggestClient,
    unreachableNotice,
    unreachableOnlyFromParam,
    viewCounts,
    viewFromParam,
} from './accountsModel';
import {
    connectAccountToClient,
    disconnectFromClient,
    fetchActiveClients,
    fetchAgencyAccounts,
    fetchCandidates,
    forgetActiveClients,
    ignoreAccount,
    isOk,
    saveSelectedActions,
} from './accountsApi';
import {platformName} from '../../../utils/filterNotes';
import {AccountLogo} from './AccountParts';
import {AccountDrawer} from './AccountDrawer';
import {CLIENTS_FAILED, CLIENTS_LOADING, CLIENTS_OK, ClientPickerButton} from './ClientPicker';
import {IgnoredView, LinkedView, UnlinkedView} from './AccountsViews';
import {ReconnectHost} from './ReconnectHost';
import {ScreenTracker} from '../../../utils/ScreenTracker';
import {reconnectPlatformName} from '../connections/reconnectAccountText';

const VIEW_LABEL_KEYS = {
    [VIEW_LINKED]: 'agency_v2.accounts.view.linked',
    [VIEW_UNLINKED]: 'agency_v2.accounts.view.unlinked',
    [VIEW_IGNORED]: 'agency_v2.accounts.view.ignored',
};

const CANDIDATES_LOADING = 'loading';
// La pantalla en los eventos del cupo (`account_quota_<acción>_accounts`).
const QUOTA_SURFACE = 'accounts';

// La confirmación de desconectar es la de hoy, según qué se desconecta (contrato §3, copy §3).
function disconnectCopy(t, row) {
    if (row.kind === KIND_PROFITABILITY) {
        return {title: t('economics.escalafy.unlink'), message: t('economics.escalafy.unlink_confirm'), question: ''};
    }
    if (row.kind === KIND_STORE) {
        return {
            title: t('client_store_row.unlink_title'),
            message: t('client_store_row.unlink_message'),
            question: t('client_store_row.unlink_question'),
        };
    }
    return {
        title: t('clients.unlink_ad_account_title'),
        message: t('clients.unlink_ad_account_message'),
        question: t('clients.unlink_ad_account_question'),
    };
}

/**
 * Conexiones › Cuentas en la UX 2.1 (tanda «Agencia 2.1», D8; contrato §3).
 *
 * Tres vistas —Conectadas a clientes (la de entrada), Sin cliente e Ignoradas— con buscador, chips
 * por plataforma y de a 50 filas por vista. Los endpoints son los de la pantalla clásica, que sigue
 * intacta detrás de `useUiVersion().isV2` (`AgencyAdAccountsAndStoresWrapper`).
 *
 * `location` y `navigate` llegan del wrapper (los de react-router): la vista y la plataforma viven
 * en la URL (`?vista=`, `?plataforma=`) y se leen de ahí en cada render, así un link de Plataformas
 * («Ver las N cuentas») o de un mail (`?ignore=`) aterriza donde tiene que aterrizar.
 */
export function AccountsV2({location, navigate}) {
    const {t} = useTranslation();
    const mounted = useRef(true);
    useEffect(() => () => { mounted.current = false; }, []);

    // ---- la URL ---------------------------------------------------------------------------------
    const search = (location && location.search) || '';
    const pathname = (location && location.pathname) || '/agency/ad-accounts';
    const params = useMemo(() => new URLSearchParams(search), [search]);
    const view = viewFromParam(params.get('vista'));
    const platform = platformFromParam(params.get('plataforma'));
    const ignoreParam = params.get('ignore');
    // `?acceso=sin`: sólo las cuentas sin acceso de líneas activas («Ver cuáles» y el Tablero, P-4).
    const unreachableParam = unreachableOnlyFromParam(params.get('acceso'));

    const writeParams = useCallback((changes) => {
        const next = new URLSearchParams(search);
        Object.keys(changes).forEach((key) => {
            const value = changes[key];
            if (value === null || value === undefined || value === '') next.delete(key);
            else next.set(key, value);
        });
        const query = next.toString();
        navigate({pathname, search: query ? `?${query}` : ''}, {replace: true});
    }, [navigate, pathname, search]);

    // ---- los datos ------------------------------------------------------------------------------
    const [accounts, setAccounts] = useState(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const loadSeq = useRef(0);
    const hasData = useRef(false);

    // El cupo de cuentas del plan (UD-15): el contador de la barra y, lleno, el cartel. Toda acción que
    // vincula o desvincula termina en `load()`, así que el refresco del cupo va ahí, una sola vez; la
    // primera carga la hace el hook al montar.
    const quota = useAdAccountQuota();
    const quotaFull = quotaIsFull(quota);
    useEffect(() => {
        if (quotaFull) recordQuotaEvent('shown', QUOTA_SURFACE);
    }, [quotaFull]);

    // Carga de pantalla → `LoadErrorState` con reintentar. Refresco después de una acción → si falla,
    // silencio y se queda lo que había (custom-components.md, el reparto por quién disparó el fetch).
    const load = useCallback(() => {
        const seq = ++loadSeq.current;
        if (hasData.current) refetchAdAccountQuota();
        return fetchAgencyAccounts().then((response) => {
            if (!mounted.current || seq !== loadSeq.current) return;
            if (response && response.status === 'ERROR_NO_AGENCY') {
                navigate('/agency-register');
                return;
            }
            if (!isOk(response) || !response.data) {
                if (!hasData.current) setLoadFailed(true);
                return;
            }
            hasData.current = true;
            setLoadFailed(false);
            setAccounts(normalizeAccounts(response.data));
        });
    }, [navigate]);

    useEffect(() => { load(); }, [load]);

    const retry = useCallback(() => {
        setLoadFailed(false);
        setAccounts(null);
        load();
    }, [load]);

    // La actividad de «Sin cliente». Si falla, la vista sale como lista simple.
    const [candidates, setCandidates] = useState({state: CANDIDATES_LOADING, byPk: null});
    useEffect(() => {
        fetchCandidates().then((response) => {
            if (!mounted.current) return;
            const byPk = candidatesByAdAccountPk(response);
            setCandidates({state: byPk ? 'ok' : 'failed', byPk});
        });
    }, []);

    // Los clientes de la agencia: el universo del buscador y de la sugerencia, y nada más.
    const [clients, setClients] = useState([]);
    const [clientsState, setClientsState] = useState(CLIENTS_LOADING);
    const loadClients = useCallback(() => {
        setClientsState(CLIENTS_LOADING);
        fetchActiveClients().then((response) => {
            if (!mounted.current) return;
            if (isOk(response) && Array.isArray(response.data)) {
                setClients(response.data);
                setClientsState(CLIENTS_OK);
            } else {
                setClientsState(CLIENTS_FAILED);
            }
        });
    }, []);
    useEffect(() => { loadClients(); }, [loadClients]);
    const ensureClients = useCallback(() => {
        if (clientsState === CLIENTS_FAILED) loadClients();
    }, [clientsState, loadClients]);

    // ---- filtros y páginas ----------------------------------------------------------------------
    const [query, setQuery] = useState('');
    const [limit, setLimit] = useState(PAGE_SIZE);
    useEffect(() => { setLimit(PAGE_SIZE); }, [view, platform, query, unreachableParam]);

    const counts = useMemo(() => (accounts ? viewCounts(accounts, platform) : null), [accounts, platform]);
    const chips = useMemo(() => (accounts ? platformChips(accounts, view, platform) : []), [accounts, view, platform]);
    // El chip «Sin acceso» vive en «Conectadas» y sólo cuando hay alguna: sin ninguna, el filtro no
    // dejaría nada que ver y se ignora (la cuenta que se reconectó desaparece de la lista sola).
    const unreachableCount = useMemo(() => (accounts && view === VIEW_LINKED
        ? filterRows(accounts[VIEW_LINKED], {platform, unreachableOnly: true}).length : 0), [accounts, view, platform]);
    const unreachableOnly = unreachableParam && unreachableCount > 0;
    const filtered = useMemo(() => (accounts ? filterRows(accounts[view], {platform, query, unreachableOnly}) : []),
        [accounts, view, platform, query, unreachableOnly]);
    // El aviso de arriba (M4): cuentas de líneas activas sin acceso, de toda la agencia.
    const notice = useMemo(() => (accounts ? unreachableNotice(accounts[VIEW_LINKED]) : null), [accounts]);
    const noticeShown = useRef(false);
    useEffect(() => {
        if (!notice || noticeShown.current) return;
        noticeShown.current = true;
        ScreenTracker.recordEvent('accounts_unreachable_notice_shown');
    }, [notice]);
    // El aviso cuenta las de toda la agencia: «Ver cuáles» saca la plataforma elegida y la búsqueda, que
    // si no podían esconderlas (con «Google» elegido y las caídas en Meta, el clic no hacía nada: C-rp-fe-03).
    const seeUnreachable = useCallback(() => {
        ScreenTracker.recordEvent('accounts_unreachable_notice_click');
        setQuery('');
        writeParams({vista: paramOfView(VIEW_LINKED), acceso: ACCESS_PARAM_NONE, plataforma: null});
    }, [writeParams]);
    const groups = useMemo(() => {
        if (view === VIEW_LINKED) return groupByClient(filtered);
        if (view === VIEW_UNLINKED) return groupUnlinked(filtered, candidates.byPk);
        return [{key: 'ignored', group: null, rows: sortIgnored(filtered)}];
    }, [view, filtered, candidates.byPk]);
    const page = useMemo(() => pageGroups(groups, limit), [groups, limit]);
    const shown = rowsIn(page);
    const total = filtered.length;
    const showMore = useCallback(() => setLimit((current) => current + PAGE_SIZE), []);

    // ---- selección (sólo «Sin cliente») ---------------------------------------------------------
    const [selected, setSelected] = useState(() => new Set());
    const unlinkedVisible = useMemo(
        () => (accounts ? filterRows(accounts[VIEW_UNLINKED], {platform, query}) : []),
        [accounts, platform, query],
    );
    // Lo que el filtro esconde sale de la selección: una acción en lote actúa sobre lo que se ve.
    useEffect(() => {
        setSelected((current) => {
            const pruned = pruneSelection(current, unlinkedVisible);
            return pruned.size === current.size ? current : pruned;
        });
    }, [unlinkedVisible]);
    const selectedRows = useMemo(() => unlinkedVisible.filter((row) => selected.has(row.key)), [unlinkedVisible, selected]);
    const toggleSelected = useCallback((key) => {
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const [busyKeys, setBusyKeys] = useState(() => new Set());
    const markBusy = useCallback((key, busy) => {
        setBusyKeys((current) => {
            const next = new Set(current);
            if (busy) next.add(key);
            else next.delete(key);
            return next;
        });
    }, []);

    // La sugerencia por nombre, sólo para las filas que se dibujan.
    const suggestions = useMemo(() => {
        if (view !== VIEW_UNLINKED || clientsState !== CLIENTS_OK) return {};
        const byKey = {};
        page.forEach((group) => group.rows.forEach((row) => {
            byKey[row.key] = suggestClient(row.name, clients);
        }));
        return byKey;
    }, [view, page, clients, clientsState]);

    // ---- acciones -------------------------------------------------------------------------------
    const accountLabel = (row) => row.name || row.accountId;
    const nameOpts = (extra) => ({...extra, interpolation: {escapeValue: false}});

    // `{ok, code, newId}`: el código tipado importa (el tope del plan se dice como tal, review C-01). Un
    // rechazo por el tope trae el cupo y se difunde; si no lo trae, se relee. Así el cartel de arriba
    // aparece solo, con los números, y el toast sólo dice qué pasó (QA-VIS-cc-230-42).
    const doConnect = useCallback((row, client, dailyLimit = null) => connectAccountToClient(
        client.id, row.channel, row.accountId, dailyLimit,
    ).then((response) => {
        const ok = isOk(response);
        if (!ok && isPlanLimitError(response)) {
            const limitQuota = quotaFromLinkError(response);
            if (limitQuota) publishAdAccountQuota(limitQuota);
            else refetchAdAccountQuota();
        }
        return {
            ok,
            code: ok ? null : errorCodeOf(response),
            newId: ok && response.data ? response.data.agency_client_ad_account_id : null,
        };
    }), []);
    const doIgnore = useCallback((row) => ignoreAccount(row.adAccountPk).then((response) => isOk(response)), []);

    // Genérico salvo el tope del plan: el `error_message` crudo puede nombrar a otra agencia. El del
    // tope es corto, porque el cartel de arriba dice el cupo y cómo seguir, con el verbo de la pantalla.
    const connectErrorText = useCallback((code) => (code === PLAN_LIMIT_CODE
        ? t('account_quota.rejected_short_accounts')
        : t('control_central.link_account_save_error')), [t]);

    const connectRow = useCallback((row, client) => {
        markBusy(row.key, true);
        doConnect(row, client).then(({ok, code}) => {
            if (!mounted.current) return;
            markBusy(row.key, false);
            if (!ok) {
                toast.error(connectErrorText(code));
                return;
            }
            toast.success(t('agency_v2.accounts.toast.connected', nameOpts({account: accountLabel(row), client: client.name})));
            load();
        });
    }, [connectErrorText, doConnect, load, markBusy, t]);

    const ignoreRow = useCallback((row) => {
        markBusy(row.key, true);
        doIgnore(row).then((ok) => {
            if (!mounted.current) return;
            markBusy(row.key, false);
            if (!ok) {
                toast.error(t('clients_v2.action_error'));
                return;
            }
            toast.success(t('agency_v2.accounts.toast.ignored', nameOpts({account: accountLabel(row)})));
            load();
        });
    }, [doIgnore, load, markBusy, t]);

    // Mientras corre un lote, las filas quedan bloqueadas (review C-05): un clic suelto mandaría otra
    // escritura en paralelo, y la selección del final pisaría lo que se tildó en el medio.
    const [bulk, setBulk] = useState(null);
    const runBulk = useCallback((action, client) => {
        const rows = selectedRows;
        if (!rows.length || bulk) return;
        setBulk({done: 0, total: rows.length});
        const worker = action === 'connect' ? (row) => doConnect(row, client) : doIgnore;
        // Con el plan lleno, las que faltan rebotarían igual: se corta en la primera y se dice una vez.
        const stopWhen = action === 'connect' ? (result) => result.code === PLAN_LIMIT_CODE : undefined;
        runInSeries(rows, worker, (done, totalCount) => {
            if (mounted.current) setBulk({done, total: totalCount});
        }, {stopWhen}).then((results) => {
            if (!mounted.current) return;
            const outcome = bulkOutcome(results);
            setBulk(null);
            setSelected(new Set(outcome.failed.map((row) => row.key)));
            const done = t('agency_v2.accounts.bulk_done', {ok: outcome.ok, total: outcome.total});
            if (!outcome.failed.length) {
                toast.success(done);
            } else if (outcome.stoppedBy === PLAN_LIMIT_CODE) {
                // Sin el «Listo:» del resumen, que sonaba a éxito con 0 conectadas (QA-VIS-cc-230-41); el
                // cupo y cómo seguir, en el cartel de arriba.
                toast.warning(t('account_quota.bulk_stopped', {ok: outcome.ok, count: outcome.total}));
            } else {
                const failedKey = action === 'connect' ? 'agency_v2.accounts.bulk_connect_failed' : 'agency_v2.accounts.bulk_ignore_failed';
                toast.warning(`${done} ${t(failedKey, {count: outcome.failed.length})}`);
            }
            load();
        });
    }, [bulk, doConnect, doIgnore, load, selectedRows, t]);

    // ---- confirmaciones, alta de cliente, drawer, reconexión ------------------------------------
    // {type: 'ignore', row} | {type: 'bulk_ignore', count} | {type: 'disconnect', row} | {type: 'move', row, client}
    const [confirm, setConfirm] = useState(null);
    // A quién se conecta el cliente que se está creando: {type: 'row'|'bulk'|'move', row}.
    const [creatingFor, setCreatingFor] = useState(null);
    const [drawerKey, setDrawerKey] = useState(null);
    // La cuenta a reconectar o a mantener, y con qué gesto: {row, kind} (ver `ReconnectHost`).
    const [repairTarget, setRepairTarget] = useState(null);
    const addAccountModal = useRef(null);

    const drawerRow = useMemo(() => {
        if (!drawerKey || !accounts) return null;
        return accounts[VIEW_LINKED].find((row) => row.key === drawerKey) || null;
    }, [accounts, drawerKey]);
    const closeDrawer = useCallback(() => setDrawerKey(null), []);
    // Los clientes donde YA está esa cuenta: «Cambiar de cliente» no los ofrece.
    const drawerLinkedClientIds = useMemo(
        () => (drawerRow && accounts ? linkedClientIdsOf(accounts[VIEW_LINKED], drawerRow) : []),
        [accounts, drawerRow],
    );

    const disconnectRow = useCallback((row) => {
        disconnectFromClient(row.raw.id).then((response) => {
            if (!mounted.current) return;
            if (!isOk(response)) {
                toast.error(t('clients_v2.action_error'));
                return;
            }
            toast.success(t('agency_v2.accounts.toast.disconnected',
                nameOpts({account: accountLabel(row), client: row.clientName})));
            setDrawerKey(null);
            load();
        });
    }, [load, t]);

    // «Cambiar de cliente»: el vínculo nuevo se lleva el límite diario y los eventos del viejo (viven
    // en el vínculo, no en la cuenta: review C-03), y RECIÉN DESPUÉS se desconecta el viejo.
    // - Si falla el alta, no se tocó nada. Con el plan lleno lo dice: hace falta un lugar libre
    //   (default; fede decide si en ese caso conviene desconectar primero).
    // - Si fallan los eventos o la baja, la cuenta quedó en los dos clientes: el drawer sigue abierto
    //   sobre el vínculo VIEJO, cuyo «Desconectar de <anterior>» hace sólo el paso que falta
    //   (review C-02), y el aviso lo dice.
    const moveRow = useCallback((row, client) => {
        const limit = dailyLimitOf(row);
        const carriedLimit = limit.state === 'set' ? limit : null;
        const actions = (row.raw && row.raw.selected_actions) || [];
        const partial = () => {
            toast.warning(t('agency_v2.accounts.toast.move_partial',
                nameOpts({account: accountLabel(row), client: client.name, previous: row.clientName})));
            load();
        };
        doConnect(row, client, carriedLimit).then(({ok, code, newId}) => {
            if (!mounted.current) return;
            if (!ok) {
                toast.error(code === PLAN_LIMIT_CODE
                    ? t('agency_v2.accounts.error.plan_limit_move', nameOpts({client: client.name, previous: row.clientName}))
                    : t('control_central.link_account_save_error'));
                return;
            }
            const copyActions = actions.length && newId
                ? saveSelectedActions(newId, actions).then((response) => isOk(response))
                : Promise.resolve(true);
            copyActions.then((actionsOk) => {
                if (!mounted.current) return;
                if (!actionsOk) {
                    partial();
                    return;
                }
                disconnectFromClient(row.raw.id).then((response) => {
                    if (!mounted.current) return;
                    if (!isOk(response)) {
                        partial();
                        return;
                    }
                    toast.success(t('agency_v2.accounts.toast.connected', nameOpts({account: accountLabel(row), client: client.name})));
                    setDrawerKey(null);
                    load();
                });
            });
        });
    }, [doConnect, load, t]);

    const handleConfirmYes = useCallback(() => {
        const action = confirm;
        setConfirm(null);
        if (!action) return;
        if (action.type === 'ignore') ignoreRow(action.row);
        else if (action.type === 'bulk_ignore') runBulk('ignore');
        else if (action.type === 'disconnect') disconnectRow(action.row);
        else if (action.type === 'move') moveRow(action.row, action.client);
    }, [confirm, disconnectRow, ignoreRow, moveRow, runBulk]);

    const handleClientCreated = useCallback((newClientId, newClientName) => {
        const target = creatingFor;
        setCreatingFor(null);
        forgetActiveClients();
        const client = {id: newClientId, name: newClientName};
        setClients((current) => current.concat([client]));
        if (!target) return;
        if (target.type === 'row') connectRow(target.row, client);
        else if (target.type === 'bulk') runBulk('connect', client);
        else if (target.type === 'move') setConfirm({type: 'move', row: target.row, client});
    }, [connectRow, creatingFor, runBulk]);

    // `?ignore=CANAL||id` (avisos y mails): abre «Sin cliente» con la confirmación de esa cuenta. Se
    // atiende una sola vez por valor, y el parámetro sale de la URL: recargar no la vuelve a abrir.
    const handledIgnore = useRef(null);
    useEffect(() => {
        if (!accounts || !ignoreParam || handledIgnore.current === ignoreParam) return;
        handledIgnore.current = ignoreParam;
        const row = findIgnoreTarget(accounts[VIEW_UNLINKED], parseIgnoreParam(ignoreParam));
        if (row) {
            writeParams({ignore: null, vista: paramOfView(VIEW_UNLINKED), plataforma: null});
            setConfirm({type: 'ignore', row});
        } else {
            writeParams({ignore: null});
        }
    }, [accounts, ignoreParam, writeParams]);

    // ---- render ---------------------------------------------------------------------------------
    const pickerProps = {clients, clientsState, onOpen: ensureClients};

    const renderToolbar = () => (
        <>
            <div className="aac-toolbar">
                <div className="uiseg aac-views" role="tablist" aria-label={t('agency_v2.accounts.views_label')}>
                    {VIEWS.map((key) => {
                        const active = key === view;
                        const warn = key === VIEW_UNLINKED && !active && counts && counts[key] > 0;
                        return (
                            <button key={key} type="button" role="tab" aria-selected={active}
                                    aria-controls="aac-view-panel"
                                    className={`uiseg__btn${active ? ' -active' : ''}`}
                                    onClick={() => writeParams({vista: paramOfView(key)})}>
                                {t(VIEW_LABEL_KEYS[key])}
                                {counts && <span className={`uiseg__count${warn ? ' -warn' : ''}`}>{counts[key]}</span>}
                            </button>
                        );
                    })}
                </div>
                <label className="aac-search">
                    <MagnifyingGlass size={15} aria-hidden="true"/>
                    <input type="text" value={query} onChange={(event) => setQuery(event.target.value)}
                           placeholder={t('agency_v2.accounts.search_placeholder')}
                           aria-label={t('agency_v2.accounts.search_placeholder')}/>
                    {query && (
                        <button type="button" className="aac-search__clear" aria-label={t('clients_v2.search_clear')}
                                onClick={() => setQuery('')}>
                            <X size={13} weight="bold" aria-hidden="true"/>
                        </button>
                    )}
                </label>
                {quota && (
                    <span className={`aac-quota${quotaFull ? ' -full' : ''}`} data-testid="aac-quota">
                        {quotaCounterText(t, quota)}
                    </span>
                )}
            </div>
            {(chips.length > 0 || unreachableCount > 0) && (
                <div className="aac-chips" role="group" aria-label={t('agency_accounts_filter.filter_by_platform')}>
                    <button type="button" className={`aac-chip${!platform ? ' -on' : ''}`} aria-pressed={!platform}
                            onClick={() => writeParams({plataforma: null})}>
                        {t('agency_v2.accounts.platform_all')}
                        {/* Todas las de la vista, con o sin plataforma elegida. */}
                        <span className="aac-chip__n">{accounts[view].length}</span>
                    </button>
                    {chips.map((chip) => (
                        <button key={chip.channel} type="button" className={`aac-chip${chip.selected ? ' -on' : ''}`}
                                aria-pressed={chip.selected} disabled={chip.disabled}
                                onClick={() => writeParams({plataforma: chip.selected ? null : chip.channel})}>
                            <AccountLogo channel={chip.channel} size={18}/>
                            {chip.label}
                            <span className="aac-chip__n">{chip.count}</span>
                        </button>
                    ))}
                    {unreachableCount > 0 && (
                        <button type="button" className={`aac-chip -warn${unreachableOnly ? ' -on' : ''}`}
                                aria-pressed={unreachableOnly} data-testid="aac-chip-unreachable"
                                onClick={() => writeParams({acceso: unreachableOnly ? null : ACCESS_PARAM_NONE})}>
                            <Warning size={14} weight="bold" aria-hidden="true"/>
                            {t('agency_v2.accounts.access.error')}
                            <span className="aac-chip__n">{unreachableCount}</span>
                        </button>
                    )}
                </div>
            )}
        </>
    );

    // El cartel cuenta lo mismo que el segmentado: la vista con la plataforma elegida, sin la
    // búsqueda (review C-07). Con un chip de Meta, «1 tiene actividad» tiene que estar en la lista.
    const renderUnlinkedIntro = () => {
        const all = filterRows(accounts[VIEW_UNLINKED], {platform});
        if (!all.length) return null;
        const active = activeUnlinkedCount(all, candidates.byPk);
        return (
            <div className="aac-callout">
                <b>{t('agency_v2.accounts.unlinked_title', {count: all.length})}</b>
                <p>{active ? t('agency_v2.accounts.unlinked_text_active', {count: active}) : t('agency_v2.accounts.unlinked_text')}</p>
            </div>
        );
    };

    const renderBulkBar = () => {
        if (!selectedRows.length && !bulk) return null;
        return (
            <div className="aac-bulk" data-testid="aac-bulk">
                <b>{t('agency_v2.accounts.bulk_selected', {count: selectedRows.length})}</b>
                {bulk && (
                    <span className="aac-bulk__progress" role="status">
                        {t('agency_v2.accounts.bulk_progress', {done: bulk.done, total: bulk.total})}
                    </span>
                )}
                <span className="aac-spacer"/>
                <ClientPickerButton {...pickerProps} className="aac-btn -sm -primary"
                                    label={t('agency_v2.accounts.connect_to_client')}
                                    suggested={null} busy={!!bulk}
                                    onPick={(client) => runBulk('connect', client)}
                                    onCreate={() => setCreatingFor({type: 'bulk'})}/>
                <button type="button" className="aac-btn -sm" disabled={!!bulk}
                        onClick={() => setConfirm({type: 'bulk_ignore', count: selectedRows.length})}>
                    {t('agency_v2.accounts.bulk_ignore')}
                </button>
                <button type="button" className="aac-btn -sm -ghost" disabled={!!bulk}
                        onClick={() => setSelected(new Set())}>
                    {t('agency_v2.accounts.bulk_cancel')}
                </button>
            </div>
        );
    };

    // Con texto buscado, «ninguna coincide con la búsqueda»; con sólo una plataforma elegida, que no
    // hay de esa plataforma en esta vista (review C-08); sin filtros, el vacío de la vista.
    let emptyText = t(`agency_v2.accounts.empty_view.${view}`);
    if (query.trim()) emptyText = t('agency_v2.accounts.empty_search');
    else if (platform) {
        emptyText = t('agency_v2.accounts.empty_platform',
            {platform: platformName(platform), interpolation: {escapeValue: false}});
    }

    const renderView = () => {
        const common = {page, shown, total, emptyText, onMore: showMore};
        if (view === VIEW_LINKED) {
            return <LinkedView {...common} onOpen={(row) => setDrawerKey(row.key)}
                               onReconnect={(row) => setRepairTarget({row, kind: reconnectKindOf(row)})}/>;
        }
        if (view === VIEW_UNLINKED) {
            return (
                <>
                    {renderUnlinkedIntro()}
                    {renderBulkBar()}
                    {candidates.state === CANDIDATES_LOADING
                        ? <div className="aac-card"><div className="box-loader"><Spinner color="positive"/></div></div>
                        : <UnlinkedView {...common} selected={selected} busyKeys={busyKeys} locked={!!bulk}
                                        suggestions={suggestions}
                                        pickerProps={pickerProps} onToggle={toggleSelected}
                                        onConnect={connectRow}
                                        onCreateClient={(row) => setCreatingFor({type: 'row', row})}
                                        onIgnore={(row) => setConfirm({type: 'ignore', row})}/>}
                </>
            );
        }
        return <IgnoredView {...common} busyKeys={busyKeys} locked={!!bulk} pickerProps={pickerProps}
                            onConnect={connectRow}
                            onCreateClient={(row) => setCreatingFor({type: 'row', row})}/>;
    };

    const renderBody = () => {
        if (loadFailed) {
            return <LoadErrorState title={t('agency_v2.accounts.load_error')}
                                   text={t('agency_v2.accounts.load_error_explanation')}
                                   retryLabel={t('agency_v2.accounts.load_error_retry')}
                                   onRetry={retry}/>;
        }
        if (!accounts) {
            return <div className="box-loader"><Spinner color="positive"/></div>;
        }
        return (
            <>
                {quotaFull && (
                    // Lleno, «Conectar a cliente» rebota en cualquier fila: se dice una vez, arriba, y
                    // cómo seguir. Liberar es acá mismo (desconectar en «Conectadas a clientes»).
                    <div className="aac-callout aac-quota-full" data-testid="aac-quota-full">
                        <b>{quotaFullTitle(t, quota)}</b>
                        <p>{t('account_quota.full_text_accounts')}</p>
                        <button type="button" className="aac-btn -sm"
                                onClick={() => requestMoreAccounts(t, quota, QUOTA_SURFACE)}>
                            {t('account_quota.request')}
                        </button>
                    </div>
                )}
                {notice && (
                    // UD-20 (M4): las cuentas de las líneas activas que dejaron de actualizarse, y con
                    // quién entrar para reconectarlas si las ve todas el mismo usuario.
                    <div className="aac-callout aac-unreachable" data-testid="aac-unreachable" role="status">
                        <Warning size={18} weight="bold" aria-hidden="true" className="aac-unreachable__icon"/>
                        <div className="aac-unreachable__tx">
                            <b>{t('agency_v2.accounts.unreachable.title', {count: notice.count})}</b>
                            {/* En singular con una sola cuenta: «La ve… Reconéctala…» (QA-VIS-rp-fe-02). */}
                            <p>{notice.userName
                                ? t('agency_v2.accounts.unreachable.text_user', {
                                    count: notice.count,
                                    platform: reconnectPlatformName(notice.channel),
                                    user: notice.userName,
                                    interpolation: {escapeValue: false},
                                })
                                : t('agency_v2.accounts.unreachable.text', {count: notice.count})}</p>
                        </div>
                        {!(unreachableOnly && view === VIEW_LINKED) && (
                            <button type="button" className="aac-btn -sm" onClick={seeUnreachable}>
                                {t('agency_v2.accounts.unreachable.see')}
                            </button>
                        )}
                    </div>
                )}
                {renderToolbar()}
                <div id="aac-view-panel" role="tabpanel">{renderView()}</div>
            </>
        );
    };

    const renderConfirm = () => {
        if (!confirm) return null;
        let copy;
        if (confirm.type === 'ignore' || confirm.type === 'bulk_ignore') {
            copy = {
                title: t('unlinked_ad_account.ignore_title'),
                message: t('unlinked_ad_account.ignore_message'),
                question: confirm.type === 'ignore'
                    ? t('unlinked_ad_account.ignore_question')
                    : t('agency_v2.accounts.bulk_ignore_question', {count: confirm.count}),
            };
        } else if (confirm.type === 'disconnect') {
            copy = disconnectCopy(t, confirm.row);
        } else {
            // Mover lleva el límite y los eventos (review C-03): el texto de desvincular («no se
            // controlará el límite diario») decía lo contrario. Una tienda no tiene ni uno ni otro.
            const moveOpts = nameOpts({previous: confirm.row.clientName});
            copy = {
                title: t('agency_v2.accounts.detail.change_client'),
                message: confirm.row.kind === KIND_STORE
                    ? t('client_store_row.unlink_message')
                    : t('agency_v2.accounts.detail.change_client_message', moveOpts),
                question: t('agency_v2.accounts.detail.change_client_question',
                    nameOpts({client: confirm.client.name, previous: confirm.row.clientName})),
            };
        }
        return <YesNoModal key={confirm.type} isOpen={true} skipRenderButton={true}
                           title={copy.title} message={copy.message} question={copy.question}
                           onYesAnswer={handleConfirmYes} onNoAnswer={() => setConfirm(null)}/>;
    };

    const cta = (
        <button type="button" className="aac-btn -primary aac-cta"
                onClick={() => addAccountModal.current && addAccountModal.current.toggle()}>
            <Plus size={14} weight="bold" aria-hidden="true"/>
            {t('agency_v2.accounts.connect_cta')}
        </button>
    );

    return (
        <section className="section-content">
            <AgencyHeader/>
            <AgencyTabs activeTab="-third"/>
            <ConnectionsSubTabs activeSubTab={CONNECTIONS_TAB_ACCOUNTS} actions={cta}/>
            <div className="section-body aac-screen" data-testid="aac-screen">
                {renderBody()}
            </div>

            {/* «Conectar una cuenta a un cliente»: el modal de siempre, con su paso de eventos. Esta pantalla tiene el cartel
                del cupo: ante el tope, el modal dice el toast corto y no repite el aviso. */}
            <AddClientAdAccountModal ref={addAccountModal} quotaNoticeOnScreen afterSuccessfulSubmit={() => {
                toast.success(t('agency_ad_accounts.account_linked_successfully'));
                load();
            }}/>

            <Modal isOpen={!!creatingFor} toggle={() => setCreatingFor(null)}>
                <ModalHeader toggle={() => setCreatingFor(null)}>{t('add_client.modal_title')}</ModalHeader>
                {creatingFor && <AddClientForm onClientAdded={handleClientCreated}/>}
            </Modal>

            {renderConfirm()}

            <ReconnectHost target={repairTarget} onClose={() => setRepairTarget(null)}
                           onDone={() => { setRepairTarget(null); load(); }}/>

            {drawerRow && (
                <AccountDrawer row={drawerRow} clients={clients} clientsState={clientsState}
                               onLoadClients={ensureClients} linkedClientIds={drawerLinkedClientIds}
                               onChangeClient={(row, client) => setConfirm({type: 'move', row, client})}
                               onCreateClient={(row) => setCreatingFor({type: 'move', row})}
                               onRepair={(row, kind) => setRepairTarget({row, kind})}
                               onDisconnect={(row) => setConfirm({type: 'disconnect', row})}
                               onEdited={() => {
                                   toast.success(t('agency_ad_accounts.account_edited_successfully'));
                                   load();
                               }}
                               onClose={closeDrawer}/>
            )}
        </section>
    );
}

export default AccountsV2;
